import { Component, Event, EventEmitter, JSX, Method, Prop, h } from '@stencil/core'

import { CheckboxEventDetail } from './air-checkbox.types'
import { KeyCode } from '../../enums/keycode.enum'
import { Validation } from '../../types/status.types'

@Component({
  tag: 'air-checkbox',
  styleUrl: 'air-checkbox.scss',
  shadow: true,
})
export class AirCheckbox {
  /** Defines name */
  @Prop({ reflect: true }) name!: string

  /** Defines optionKey */
  @Prop() optionKey!: string | number | boolean

  /** Defines label */
  @Prop() label!: string

  /** Defines disabled */
  @Prop() disabled = false

  /** Defines if checkbox is required */
  @Prop() required = false

  /** Defines if checkbox is checked */
  @Prop({ mutable: true, reflect: true }) checked = false

  /** Defines if checkbox should have autofocus */
  // eslint-disable-next-line @stencil-community/reserved-member-names
  @Prop() autofocus = false

  /** Specifies validation of checkbox */
  @Prop() validation?: Validation

  private checkboxElement?: HTMLElement
  private checkboxEventDetail?: CheckboxEventDetail

  /** Emits value on change */
  @Event({ eventName: 'air-change' }) change!: EventEmitter<CheckboxEventDetail>

  /** Emits value on focus */
  @Event({ eventName: 'air-focus' }) focus!: EventEmitter<CheckboxEventDetail>

  /** Emits value on blur */
  @Event({ eventName: 'air-blur' }) blur!: EventEmitter<CheckboxEventDetail>

  /** To focus checkbox */
  @Method()
  async setFocus(): Promise<void> {
    this?.checkboxElement?.focus()
  }

  async componentDidLoad(): Promise<void> {
    this.autofocus && (await this.setFocus())
  }

  private handleClick = (event: MouseEvent): void => {
    event.stopPropagation()
    if (this.disabled) return
    this.checked = !this.checked
    this.checkboxEventDetail = {
      name: this.name,
      optionKey: this.optionKey,
      checked: this.checked,
    }
    this.change?.emit(this.checkboxEventDetail)
  }

  private handleFocus = (event: FocusEvent): void => {
    event.stopPropagation()
    if (this.disabled) return
    this.checkboxEventDetail = {
      name: this.name,
      optionKey: this.optionKey,
      checked: this.checked,
    }
    this.focus?.emit(this.checkboxEventDetail)
  }

  private handleBlur = (event: FocusEvent): void => {
    event.stopPropagation()
    if (this.disabled) return
    this.checkboxEventDetail = {
      name: this.name,
      optionKey: this.optionKey,
      checked: this.checked,
    }
    this.blur?.emit(this.checkboxEventDetail)
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    event.stopPropagation()
    if (event.code === KeyCode.Space) {
      event.preventDefault()
      this.checked = !this.checked
      this.checkboxEventDetail = {
        name: this.name,
        optionKey: this.optionKey,
        checked: this.checked,
      }
      this.change?.emit(this.checkboxEventDetail)
    }
  }

  private renderLabel(): HTMLSpanElement | null {
    return this.label !== undefined ? (
      <span class="label">
        {this.label}
        {this.required && <span class="required-mark">*</span>}
      </span>
    ) : null
  }

  render(): JSX.Element {
    const statusType = this.validation?.status
    const classes = {
      checkbox: true,
      disabled: this.disabled,
      [`status-${statusType}`]: statusType !== undefined,
    }
    const attributes = {
      class: classes,
      role: 'checkbox',
      'aria-checked': this.checked,
      tabindex: this.disabled ? '-1' : '0',
      onClick: this.handleClick,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      onKeydown: this.handleKeyDown,
      ref: (el: HTMLDivElement | undefined): void => {
        this.checkboxElement = el
      },
    }
    return <div {...attributes}>{this.renderLabel()}</div>
  }
}
